import { ChevronRight } from 'lucide-react';
import { renderChatMarkdown } from '../../lib/chat-markdown';
import { MarkdownView } from '../MarkdownView';
import { buildChatTranscript, type ChatCursorEvent } from './chat-transcript';

const muted = 'text-xs text-gray-500 dark:text-gray-400';
const summaryClass = `${muted} flex cursor-pointer list-none items-center gap-1 select-none [&::-webkit-details-marker]:hidden`;
const resultBox = 'mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded-md border border-gray-200 px-3 py-2 font-sans text-xs text-gray-700 dark:border-gray-700 dark:text-gray-300';

const SHELL_TOOLS = new Set(['shell', 'run_terminal_cmd']);
const FILE_LABELS: Record<string, string> = {
  read: 'Read',
  read_file: 'Read',
  file_search: 'Search',
  write: 'Write',
  edit: 'Edit',
  edit_file: 'Edit',
  delete: 'Delete',
  grep: 'Grep',
  grep_search: 'Grep',
  glob: 'Glob',
};

function thoughtLabel(seconds: number): string {
  if (seconds <= 0) return 'Thought';
  return seconds === 1 ? 'Thought for 1 second' : `Thought for ${seconds} seconds`;
}

function prettyOutput(output: string): string {
  const text = output.trim();
  if (!text.startsWith('{') && !text.startsWith('[')) return output;
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return output;
  }
}

export function ChatTranscript({ content, events }: { content: string; events?: ChatCursorEvent[] }) {
  const blocks = buildChatTranscript(events, content);
  if (!blocks.length) return null;
  return (
    <div className="space-y-2">
      {blocks.map((block, index) => {
        if (block.kind === 'text') return <MarkdownView key={`text-${index}`} html={renderChatMarkdown(block.text)} />;
        if (block.kind === 'thinking') {
          return (
            <details key={`thinking-${index}`} className="group">
              <summary className={summaryClass}><span>{thoughtLabel(block.seconds)}</span><ChevronRight className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90" /></summary>
              {block.text ? <p className={`mt-1 whitespace-pre-wrap pl-3 ${muted}`}>{block.text}</p> : null}
            </details>
          );
        }
        if (block.kind === 'tool') {
          const isShell = SHELL_TOOLS.has(block.name);
          const verb = block.status === 'running' ? 'Using' : 'Used';
          const fileLabel = FILE_LABELS[block.name];
          const shortDetail = block.detail.split('\n')[0]?.trim() || '';
          const summaryDetail = shortDetail.length > 120 ? `${shortDetail.slice(0, 120)}…` : shortDetail;
          const label = isShell
            ? `$ ${summaryDetail || block.name}`
            : fileLabel
              ? `${fileLabel}${summaryDetail ? ` ${summaryDetail}` : ''}`
              : block.provider
                ? `${verb} ${block.name} with ${block.provider}`
                : `${verb} ${block.name}`;
          const outputBody = block.output ? prettyOutput(block.output) : '';
          if (!block.detail && !outputBody) return <p key={`tool-${index}`} className={muted}>{label}</p>;
          return (
            <details key={`tool-${index}`} className="group">
              <summary className={summaryClass}><span>{label}</span><ChevronRight className="h-3 w-3 shrink-0 transition-transform group-open:rotate-90" /></summary>
              {block.detail ? <pre className={resultBox}>{block.detail}</pre> : null}
              {outputBody ? <pre className={resultBox}>{outputBody}</pre> : null}
            </details>
          );
        }
        if (block.kind === 'task') return <p key={`task-${index}`} className={muted}>{block.title}</p>;
        return <p key={`status-${index}`} className={muted}>{block.text}</p>;
      })}
    </div>
  );
}
